import type { CreateDiscountBody, DeviceRef, DiscountFormValues } from './types';

// Two shared picker shapers (device carries {id,isComponent}; bundle is id-only). `all` → empty array.
const device = (p: { all: boolean; ids: DeviceRef[] }) => ({
  type: 'device' as const,
  allDevices: p.all,
  devices: p.all ? [] : p.ids,
});
const bundle = (p: { all: boolean; ids: string[] }) => ({
  type: 'bundle' as const,
  allBundles: p.all,
  bundleIds: p.all ? [] : p.ids,
});

/** Serialize the whole three-step wizard form to the `POST /discounts/admin` body (grilling "Key wire
 *  shapes"): flags for "All", `{id,isComponent}` device refs, id-only bundle/customer arrays,
 *  `YYYY-MM-DD` dates, numeric tier qty/value. Pure — unit-tested. */
export function toCreateBody(v: DiscountFormValues): CreateDiscountBody {
  const applicability: CreateDiscountBody['applicability'] =
    v.applicability.type === 'device'
      ? device(v.applicability.devices)
      : v.applicability.type === 'bundle'
        ? bundle(v.applicability.bundles)
        : {
            type: 'customer',
            allCustomers: v.applicability.customers.all,
            customerIds: v.applicability.customers.all ? [] : v.applicability.customers.ids,
            applicableTo:
              v.applicability.applicableTo.type === 'all_products'
                ? { type: 'all_products' }
                : v.applicability.applicableTo.type === 'device'
                  ? device(v.applicability.applicableTo.devices)
                  : bundle(v.applicability.applicableTo.bundles),
          };

  // Create is gated on a non-null, valid trigger, so `v.trigger` is present (and Limited has a window).
  const t = v.trigger!;
  const window = (r: [string, string]) => ({ from: r[0], to: r[1] });
  const trigger: CreateDiscountBody['trigger'] =
    t.type === 'first_time_customer'
      ? { type: 'first_time_customer' }
      : t.type === 'limited_period'
        ? { type: 'limited_period', dateRange: window(t.dateRange!) }
        : t.dateRange
          ? { type: 'once_per_customer', dateRange: window(t.dateRange) }
          : { type: 'once_per_customer' };

  const quantityRule: CreateDiscountBody['quantityRule'] =
    v.quantityRule.type === 'fixed'
      ? { type: 'fixed' }
      : {
          type: 'quantity_based',
          tierBehavior: v.quantityRule.tierBehavior,
          tiers: v.quantityRule.tiers.map((tier) => ({
            minQty: Number(tier.minQty),
            maxQty: Number(tier.maxQty),
            value: Number(tier.value),
          })),
        };

  return {
    name: v.name.trim(),
    discountFormat: v.discountFormat,
    value: Number(v.value),
    ...(v.description.trim() ? { description: v.description.trim() } : {}),
    applicability,
    trigger,
    quantityRule,
  };
}
